import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HubShell } from "../../components/hub/shell";
import { ErrorState } from "../../components/shared/StateMessage";
import PanelErrorBoundary from "../../components/shared/PanelErrorBoundary";
import { NotPermitted, ClientSlideOut } from "../../components/hub/_shims";
import NowRow from "../../components/hub/now/NowRow";
import PromisePanel from "../../components/hub/now/PromisePanel";
import CreateTaskModal from "../../components/hub/work/overlays/CreateTaskModal";
import { createdTaskOf, toWorkTypeOptions } from "../../components/hub/work/utils";
import { NEW_ROW_MS, SEEN_GRACE_MS } from "../../components/hub/now/newRows";
import { splitLeadSentence } from "../../components/hub/now/leadSentence";
import { tintClasses } from "../../components/shared/tints";
import { fmtNumber } from "../../components/shared/format";
import {
  getNow,
  dismissNow,
  markPromiseMet,
  chaseClient,
  releaseAgent,
  editAgent,
  openRequest,
  linkPromiseTask,
} from "../../api/hub/now";
import { createWork, getWorkTypes } from "../../api/hub/work";
import { getAssignableMembers } from "../../api/conversations";

// One ranked feed of everything that needs a person, ordered by what it costs to
// ignore (rank), NOT by due date or recency. Rank is the only sort — render items[]
// as returned; never re-sort, never group by kind.

/**
 * Tasks made from a promise whose link to it did not go through, by promise id: the task's
 * id, or null when the create answered without a readable id. It lives at module scope so
 * it survives the page unmounting (the panel's Open conversation and Open task links leave
 * Now), and so a promise that already has its task is never offered "Make it a task" again.
 * An entry is deleted when a link succeeds or a load shows that task on the promise.
 */
const unlinkedTasks = new Map();

/** Empties the unlinked-task store. Exists as a test seam: the store outlives any one render. */
export function resetUnlinkedTasks() {
  unlinkedTasks.clear();
}

// What a failed link says; a load that shows the task on the promise clears a message that
// starts with it, since the link evidently went through.
const LINK_FAILED = "Task created, but linking it to this promise failed: ";

const DISMISS_UNDO_MS = 6000; // the at-rank Undo window before the dismiss commits
// The populated list sits on the Clients list's surface: a bordered, rounded panel that clips
// its rows (inset focus rings stay inside it). The empty list, which only holds the cleared
// message's space, gets none.
const LIST_SURFACE =
  "overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900";
// Each item carries the divider under it, as in the Clients list, and the last has none so it
// does not double the panel's border. On the item rather than the row, so an Undo strip in the
// same slot is divided too.
const ROW_DIVIDER = "border-b border-gray-100 last:border-b-0 dark:border-gray-800";
const MAX_TIMEOUT_MS = 2 ** 31 - 1; // the longest delay setTimeout accepts
const POLL_MS = 60000; // background poll → "N new" pill; never reorder under the cursor
// Rows per server page, as on the Clients list. A page is one fn_now window: limit PAGE_SIZE at
// offset (page - 1) * PAGE_SIZE.
const PAGE_SIZE = 25;

// Gated: a non-ops caller's fn_now returns nothing (null) or a permitted:false payload.
// DISTINCT from empty (a real, permitted `{items:[], total:0}` → the success state).
const isGated = (payload) => payload == null || payload.permitted === false;

// The takeaway's accent bar and marker colour by the backend's tint. Local rather than the
// shared tint vocabulary, which has no amber: a warm takeaway reads amber here while the
// row rails keep red for critical. An unknown or missing tint reads as the calm default.
const TAKEAWAY_MARK = {
  warm: "bg-amber-500",
  cool: "bg-gray-400",
  positive: "bg-emerald-500",
};
const takeawayMark = (tint) =>
  Object.prototype.hasOwnProperty.call(TAKEAWAY_MARK, tint)
    ? TAKEAWAY_MARK[tint]
    : TAKEAWAY_MARK.cool;

// The takeaway is the page's one insight surface — deliberately NOT a card. It is a
// single sentence on a plain white surface with a thin border, a small square marker
// before the text and a left accent bar, both in the backend's own tint (warm/cool/
// positive), with no heading, icon, or dismiss: one distinct line that stands out above
// the plain feed, never a notification card competing with the rows. The text is the
// server's sentence, verbatim: its lead (up to the first " — " or the end of the first
// sentence, whichever comes first) is only set in bold, never reworded, so the rendered
// text is byte-identical to `says`.
function InsightCard({ says, tint }) {
  const mark = takeawayMark(tint);
  const split = splitLeadSentence(says);
  return (
    <div
      role="region"
      aria-label="Now insight"
      className="relative mt-4 flex items-start gap-2.5 overflow-hidden rounded-xl border border-gray-200 bg-white py-3.5 pl-5 pr-4 text-[15px] font-normal leading-relaxed text-gray-700 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300"
    >
      <span
        data-insight-accent
        aria-hidden="true"
        className={`absolute inset-y-0 left-0 w-1 ${mark}`}
      />
      <span
        data-insight-marker
        aria-hidden="true"
        className={`mt-2 h-2 w-2 shrink-0 ${mark}`}
      />
      <span className="min-w-0">
        {split ? (
          <>
            <strong className="font-semibold text-gray-900 dark:text-gray-100">{split.lead}</strong>
            {split.rest}
          </>
        ) : (
          says
        )}
      </span>
    </div>
  );
}

// Nothing to do reads as a win, not an empty inbox. When Mine is empty but the team has
// work, offer the way across instead of leaving the reader on a dead end.
function AllClear({ teamCount, onShowTeam }) {
  const c = tintClasses("positive");
  return (
    <div
      data-testid="now-all-clear"
      className={`mt-6 rounded-xl border px-4 py-6 text-center ${c.surface}`}
    >
      <p className={`text-sm font-medium ${c.text}`}>All clear — nothing needs you.</p>
      {onShowTeam && (
        <button
          type="button"
          data-testid="now-team-link"
          onClick={onShowTeam}
          className="mt-2 text-sm font-medium text-sky-700 hover:underline dark:text-sky-300"
        >
          {`${teamCount} across the team →`}
        </button>
      )}
    </div>
  );
}

// The error state mirrors the insight card's shape (icon · heading · body) but in a red
// theme, so a failed load reads as the same kind of surface as the takeaway.
function ErrorCard({ onRetry }) {
  return (
    <div
      role="alert"
      className="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800/60 dark:bg-red-900/20"
    >
      <div className="flex items-start gap-3">
        <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500 dark:text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <div className="min-w-0 flex-1">
          <h3 className="mb-1 text-sm font-semibold text-red-800 dark:text-red-300">
            Temporarily unavailable
          </h3>
          <p className="text-sm leading-relaxed text-red-700 dark:text-red-300">
            This view isn&apos;t loading right now. Please try again shortly.
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-3 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 dark:border-red-800 dark:bg-transparent dark:text-red-300 dark:hover:bg-red-900/30"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}

// Outcome panels for the at-rank strip. Dismiss ("No longer needed") reads red (a promise
// pulled back); Done reads green (a promise kept). Same shape as the insight card, tinted by
// outcome.
const OUTCOME = {
  dismiss: {
    box: "border-red-200 bg-red-50 dark:border-red-800/60 dark:bg-red-900/20",
    icon: "text-red-500 dark:text-red-400",
    text: "text-red-700 dark:text-red-300",
    undo: "text-red-700 hover:text-red-800 dark:text-red-300 dark:hover:text-red-200",
  },
  met: {
    box: "border-green-200 bg-green-50 dark:border-green-800/60 dark:bg-green-900/20",
    icon: "text-green-600 dark:text-green-400",
    text: "text-green-700 dark:text-green-300",
    undo: "text-green-700 hover:text-green-800 dark:text-green-300 dark:hover:text-green-200",
  },
};

function UndoStrip({ entry, onUndo }) {
  const { item, tone, label } = entry;
  const c = OUTCOME[tone] || OUTCOME.dismiss;
  return (
    <div
      data-undo-strip={item.id}
      data-rank={item.rank}
      className={`my-2 flex items-center justify-between gap-3 rounded-xl border px-4 py-3 ${c.box}`}
    >
      <div className="flex items-center gap-3">
        <svg
          className={`h-5 w-5 flex-shrink-0 ${c.icon}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {tone === "met" ? (
            <>
              {/* check-circle — a promise kept */}
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </>
          ) : (
            <>
              {/* x-circle — a promise dismissed */}
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </>
          )}
        </svg>
        <span className={`text-sm font-medium ${c.text}`}>{label}</span>
      </div>
      <button
        type="button"
        data-undo={item.id}
        className={`text-sm font-semibold transition-colors ${c.undo}`}
        onClick={() => onUndo(item)}
      >
        Undo
      </button>
    </div>
  );
}

export default function NowPage() {
  // The scope switch always renders. fn_now(p_everyone := true) enforces its own wall
  // server-side — it returns only what the caller may see — so the control is harmless
  // for someone with no wider visibility, and asking a role number would be a weaker,
  // spoofable test than the server's own gate (00_CONTRACT §1: ask the DB, never infer).

  const [everyone, setEveryone] = useState(false);
  const [status, setStatus] = useState("loading"); // loading | ready | gated | error
  // Both scopes' totals are tracked so each segment of the scope switch can show its own
  // count. Each scope's total comes from its own fn_now call; the non-active scope is fetched
  // total-only. The active scope's total also sets the page count, the header count and the
  // sidebar "Now" badge.
  const [totals, setTotals] = useState({ mine: null, everyone: null });
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1); // 1-based; the page shown, or the one being fetched
  // The server offset the page shown (or being fetched) starts at. It is the page's index times
  // PAGE_SIZE until the reader clears rows: the server's list then shifts up under it, so the
  // next page starts earlier by the rows cleared (see goToPage). Rows other people clear shift
  // it too, which an offset pager cannot see — a page boundary can then repeat or skip a row.
  const [pageOffset, setPageOffset] = useState(0);
  const [takeaway, setTakeaway] = useState(null);
  const [pending, setPending] = useState({}); // id -> item currently showing an Undo strip
  const [incoming, setIncoming] = useState(null); // { items, count, total, takenAt } backing the "N new" pill
  const [actionError, setActionError] = useState(null); // verbatim refusal from a gated RPC
  // A scope switch or a page change refetches only the feed, never the page around it.
  const [feedLoading, setFeedLoading] = useState(false);
  // The last page request failed: the list shows an error for the requested page instead of
  // the previous page's (or scope's) rows under the new page number. Try again refetches it.
  const [feedFailed, setFeedFailed] = useState(false);
  // Only the newest page request may write state; an older one that resolves late is dropped.
  const requestRef = useRef(0);
  // Set when a scope switch fails and cleared when a page next loads: until then the new scope
  // is only half set up, so every request after it (a retry, a page change) finishes the reset.
  const failedResetRef = useRef(false);
  // Rows the reader cleared from the page shown since it was fetched; the server's list has
  // shifted up by that many, which the next page's offset allows for.
  const removedOnPageRef = useRef(0);
  const itemsRef = useRef([]);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);
  // Set the moment a page request starts and cleared when the newest one ends. State alone
  // lags a render, so an effect that runs in the same commit as the request's start would
  // still see the feed idle and start another over it.
  const requestOutRef = useRef(false);
  // Rows the reader has acted on since the page was fetched. The poll's snapshot can be up
  // to a minute old, so applying it must not resurrect a row that has since left
  // (removedRef: id → when it left) or revert an owner that has since been assigned
  // (patchesRef: id → the local patch and when it was made).
  const removedRef = useRef(new Map());
  const patchesRef = useRef(new Map());
  // The ids of the first page (offset 0) as last displayed, locally removed ones dropped; null
  // while a new scope has not shown one yet. The poll fetches the first page whichever page
  // the reader is on, and a row is "new" when it is not in this set. Known limitation: a row
  // that merely moves up from page 2 into page 1 on a rank change reads as new, as a row not
  // on screen always did.
  const page1KnownRef = useRef(new Set());
  // Rows applied through the pill and still tinted (id → when the pill was applied). Held in
  // a ref with a counter to re-render, and ONE timer aimed at the earliest expiry, so a
  // highlight needs no per-row timer. Nothing is persisted: a reload starts clean.
  const freshRef = useRef(new Map());
  const [freshTick, setFreshTick] = useState(0);
  const bumpFresh = useCallback(() => setFreshTick((n) => n + 1), []);
  const appliedAtRef = useRef(-Infinity); // when the pill was last applied, for the pointer grace
  // True only while the page itself moves focus onto the first row after applying the pill:
  // that is not the reader seeing the row, so it must not clear the tint.
  const programmaticFocusRef = useRef(false);
  const resetLocalChanges = useCallback(() => {
    removedRef.current.clear();
    patchesRef.current.clear();
    freshRef.current.clear();
    bumpFresh();
  }, [bumpFresh]);
  const timersRef = useRef({});
  const listRef = useRef(null);
  // Set when the "N new" pill is applied, so the effect below can move focus once the
  // fresh rows have rendered.
  const focusFirstRef = useRef(false);
  // Set when a page change lands, so the effect below can bring the list's top into view.
  const scrollTopRef = useRef(false);
  // Scope is read through a ref by the loaders so switching Mine/Everyone refetches ONLY
  // the feed below — the header, insight and tabs never blank out.
  const everyoneRef = useRef(everyone);
  const didMountRef = useRef(false);
  // The last empty-page refetch (scope, page and total it was for), so a server that keeps
  // answering an empty page for a non-zero total is asked once, not in a loop.
  const autoRefetchRef = useRef(null);
  // The page on screen and its server offset, for a refetch that runs after an await.
  const pageRef = useRef(1);
  const pageOffsetRef = useRef(0);
  pageRef.current = page;
  pageOffsetRef.current = pageOffset;

  // ─── Client slide-out ─────────────────────────────────────────────────────────
  // One slide-out for the whole list, outside it, opened by a row's client link. Rendered here
  // rather than in each row so the list's dimming, a row unmounting or a row's hover never
  // reach it.
  const [openClient, setOpenClient] = useState(null); // { clientId, clientName, seq }
  const clientSeqRef = useRef(0);
  // Each open takes a new `seq`, which keys the boundary below: a crashed slide-out is replaced
  // by the next open, of the same client or another.
  const showClient = useCallback((client) => {
    clientSeqRef.current += 1;
    setOpenClient({ ...client, seq: clientSeqRef.current });
  }, []);

  // ─── Promise panel ────────────────────────────────────────────────────────────
  // `openPromise` captures the clicked row's id and `dismiss_label`, so the panel keeps them
  // after the row has left the list. Every write from the panel targets THIS id, never
  // anything read from the detail payload. The rest of this state belongs to the open
  // promise and is cleared when the panel closes or another opens.
  const [openPromise, setOpenPromise] = useState(null); // { id, dismissLabel }
  const [panelSeq, setPanelSeq] = useState(0); // bumped per open, so a crashed panel resets
  const [panelReloadKey, setPanelReloadKey] = useState(0);
  const [panelBusy, setPanelBusy] = useState(false);
  const [panelError, setPanelError] = useState(null);
  const [taskDraft, setTaskDraft] = useState(null); // { id, p }: the promise's row id and detail while the task form is open
  // Promises with a create, link or write running right now, so closing and reopening the
  // panel can never offer a second create or enable stale buttons. `bumpTasks` re-renders on
  // a change here or in the module's `unlinkedTasks`.
  const inFlightRef = useRef(new Set());
  const [, setTaskTick] = useState(0);
  const bumpTasks = useCallback(() => setTaskTick((n) => n + 1), []);
  const [taskFormOptions, setTaskFormOptions] = useState(null); // loaded on first "Make it a task"
  const openPromiseRef = useRef(null);
  openPromiseRef.current = openPromise;
  const taskFormLoadingRef = useRef(false);
  const headingRef = useRef(null);
  // The promise whose row held focus when the panel opened, so closing hands it back.
  const restoreFocusRef = useRef(null);

  /**
   * A polled snapshot, minus rows the reader removed after it was taken, with the reader's
   * own owner patches laid over it. Taken-at is when the request started, so a row removed
   * (or assigned) while the request was in flight is covered too. A patch older than the
   * snapshot is dropped instead: the server has seen it by then, so if the owner differs
   * someone changed it since and the server wins.
   */
  const reconcileSnapshot = useCallback((rows, takenAt) => {
    return rows
      .filter((r) => !((removedRef.current.get(r.id) ?? -Infinity) >= takenAt))
      .map((r) => {
        const entry = patchesRef.current.get(r.id);
        if (!entry) return r;
        if (entry.at >= takenAt) return { ...r, ...entry.patch };
        patchesRef.current.delete(r.id);
        return r;
      });
  }, []);

  // Full-page load: mount + retry only. Reads scope from a ref so it never re-runs on a
  // tab switch — that path is the feed-only refetch below.
  const loadAll = useCallback(async () => {
    requestRef.current += 1; // supersedes any page request still out
    requestOutRef.current = false;
    setStatus("loading");
    setActionError(null);
    try {
      const active = everyoneRef.current;
      // fn_now now returns the `takeaway` key inline — the SAME {tint, says}
      // object fn_now_takeaway returned (perf memo §4.1) — so the feed and the
      // takeaway are ONE request, not two. The separate fn_now_takeaway call is
      // gone: one fewer round trip (~520 ms warm, ~1.6 s cold) and the eleven
      // detectors no longer run twice server-side.
      const [now, otherProbe] = await Promise.all([
        getNow({ everyone: active, limit: PAGE_SIZE, offset: 0 }),
        // Total-only probe of the other scope, so its segment shows its own count.
        // fn_now's `total` is scope-wide regardless of p_limit, so ask for one row.
        // A probe that fails is `undefined`, not gated: its count is unknown, not zero.
        getNow({ everyone: !active, limit: 1 }).catch(() => undefined),
      ]);
      if (isGated(now)) {
        setStatus("gated");
        return;
      }
      const rows = Array.isArray(now.items) ? now.items : [];
      setItems(rows);
      setPage(1);
      setPageOffset(0);
      removedOnPageRef.current = 0;
      failedResetRef.current = false;
      setFeedLoading(false);
      setFeedFailed(false);
      page1KnownRef.current = new Set(rows.map((r) => r.id));
      resetLocalChanges();
      const activeTotal = now?.total ?? 0;
      const otherTotal =
        otherProbe === undefined ? null : isGated(otherProbe) ? 0 : otherProbe.total ?? 0;
      setTotals(
        active
          ? { everyone: activeTotal, mine: otherTotal }
          : { mine: activeTotal, everyone: otherTotal },
      );
      setTakeaway(now?.takeaway && now.takeaway.says ? now.takeaway : null);
      setIncoming(null);
      setStatus("ready");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[NowPage] load failed", e);
      setStatus("error");
    }
  }, [resetLocalChanges]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // A request still out when the page unmounts must not write state.
  useEffect(
    () => () => {
      requestRef.current += 1;
    },
    [],
  );

  /**
   * Fetches one server page of the active scope into the list. Only the newest request
   * writes state (a scope switch, another page, or applying the pill supersedes it), so a
   * response that resolves late is dropped. While it is out the list dims and the pager
   * disables. On success the rows replace the list, with the reader's own removals and
   * owner patches laid over them; on failure the list shows an error for THIS page (never the
   * previous page's rows under the new number) and fetching the same page and offset again
   * retries it.
   * @param {number} target the 1-based page to fetch
   * @param {{offset?: number, reset?: boolean, probeOther?: boolean, scroll?: boolean}} [opts]
   *   `offset`: the server offset, the page's index times PAGE_SIZE unless given; `reset`: a
   *   new scope, so local changes, tints and the pill are dropped (a scope switch that failed
   *   makes every later request reset too); `probeOther`: also re-read the other scope's
   *   total; `scroll`: bring the list's top into view once it lands.
   */
  const loadPage = useCallback(
    async (
      target,
      { offset = (target - 1) * PAGE_SIZE, reset = false, probeOther = false, scroll = false } = {},
    ) => {
      const token = (requestRef.current += 1);
      requestOutRef.current = true;
      const active = everyoneRef.current;
      const takenAt = Date.now();
      const doReset = reset || failedResetRef.current;
      setPage(target);
      setPageOffset(offset);
      setFeedLoading(true);
      setFeedFailed(false);
      setActionError(null);
      if (doReset) {
        // The pill and the first-page ids belong to the scope being left.
        setIncoming(null);
        page1KnownRef.current = null;
      }
      try {
        const [now, other] = await Promise.all([
          getNow({ everyone: active, limit: PAGE_SIZE, offset }),
          probeOther
            ? getNow({ everyone: !active, limit: 1 }).catch(() => null)
            : Promise.resolve(null),
        ]);
        if (token !== requestRef.current) return;
        if (isGated(now)) {
          setStatus("gated");
          return;
        }
        if (doReset) resetLocalChanges();
        failedResetRef.current = false;
        removedOnPageRef.current = 0;
        const rows = reconcileSnapshot(Array.isArray(now.items) ? now.items : [], takenAt);
        const total = now.total ?? 0;
        setItems(rows);
        setTotals((t) => ({
          ...t,
          [active ? "everyone" : "mine"]: total,
          ...(other && !isGated(other) ? { [active ? "mine" : "everyone"]: other.total ?? 0 } : {}),
        }));
        if (offset === 0) {
          page1KnownRef.current = new Set(rows.map((r) => r.id));
          // The first page just read is newer than any snapshot behind the pill: what the
          // pill announced may be on screen now.
          setIncoming((cur) => {
            if (!cur || doReset) return null;
            const count = cur.items.filter((n) => !page1KnownRef.current.has(n.id)).length;
            return count > 0 ? { ...cur, count } : null;
          });
        }
        if (scroll) scrollTopRef.current = true;
      } catch (e) {
        if (token !== requestRef.current) return;
        // eslint-disable-next-line no-console
        console.error("[NowPage] page fetch failed", e);
        failedResetRef.current = doReset;
        setItems([]);
        setFeedFailed(true);
      } finally {
        if (token === requestRef.current) {
          requestOutRef.current = false;
          setFeedLoading(false);
        }
      }
    },
    [reconcileSnapshot, resetLocalChanges],
  );

  // Tab switch (Mine/Everyone): refetch ONLY the feed, from its first page. status stays
  // "ready", so the header, insight and tabs keep rendering while the list dims and swaps.
  // Skips the initial mount (loadAll already ran) and leaves the takeaway alone
  // (scope-agnostic).
  useEffect(() => {
    everyoneRef.current = everyone;
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    loadPage(1, { reset: true });
  }, [everyone, loadPage]);

  // Background poll, polite by construction. It never touches `items`: new rows only
  // surface as the "N new" pill and reorder when the reader clicks it. Each tick reads the
  // FIRST page whichever page the reader is on, and DOES refresh both scopes' totals
  // silently, so the page count, the tab counts and the sidebar badge stay current. Ticks
  // are skipped while the tab is hidden, and one runs as soon as it becomes visible again (an
  // interval alone would leave the counts stale until the next 60s).
  useEffect(() => {
    if (status !== "ready" || feedFailed) return undefined;
    let cancelled = false;
    let inFlight = false;
    const tick = async () => {
      if (document.hidden || inFlight) return;
      inFlight = true;
      const takenAt = Date.now();
      try {
        const [next, other] = await Promise.all([
          // Never shares another caller's in-flight read: the result is stamped with this
          // tick's own start time, which must not be later than the data's.
          getNow({ everyone, limit: PAGE_SIZE, offset: 0, dedupe: false }),
          // total-only probe of the other scope
          getNow({ everyone: !everyone, limit: 1 }).catch(() => null),
        ]);
        if (cancelled || isGated(next)) return;
        const nextItems = reconcileSnapshot(
          Array.isArray(next.items) ? next.items : [],
          takenAt,
        );
        const nextTotal = next.total ?? 0;
        setTotals((t) => ({
          ...t,
          [everyone ? "everyone" : "mine"]: nextTotal,
          ...(isGated(other)
            ? {}
            : { [everyone ? "mine" : "everyone"]: other.total ?? 0 }),
        }));
        const known = page1KnownRef.current;
        if (known === null) {
          // A scope whose first page was never shown (its switch failed): this is the first
          // page the reader can be compared with, so nothing is new yet.
          page1KnownRef.current = new Set(nextItems.map((r) => r.id));
          setIncoming(null);
          return;
        }
        const newCount = nextItems.filter((n) => !known.has(n.id)).length;
        // A tick that finds nothing new also clears an earlier pill: what it announced
        // is no longer true (the row was dealt with elsewhere, or is gone).
        setIncoming(
          newCount > 0
            ? { items: nextItems, count: newCount, total: nextTotal, takenAt }
            : null,
        );
      } catch {
        /* background poll — swallow; the next tick retries */
      } finally {
        inFlight = false;
      }
    };
    // tick() itself returns while the tab is hidden, so a hide event fetches nothing.
    const t = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [status, everyone, feedFailed, reconcileSnapshot]);

  // One timer, aimed at the earliest expiry: when it fires it drops what has expired and
  // re-renders, and the counter change reschedules it for the next one.
  useEffect(() => {
    const fresh = freshRef.current;
    if (fresh.size === 0) return undefined;
    let earliest = Infinity;
    fresh.forEach((at) => {
      earliest = Math.min(earliest, at + NEW_ROW_MS);
    });
    // setTimeout wraps any delay past 2^31-1 ms to ~immediate, which would spin if the clock
    // ever jumped back; clamp, and the firing below retargets at what is still left.
    const delay = Math.min(Math.max(0, earliest - Date.now()), MAX_TIMEOUT_MS);
    const timer = setTimeout(() => {
      const now = Date.now();
      fresh.forEach((at, id) => {
        if (now - at >= NEW_ROW_MS) fresh.delete(id);
      });
      bumpFresh();
    }, delay);
    return () => clearTimeout(timer);
  }, [freshTick, bumpFresh]);

  // The reader has seen this row (pointed at, clicked or focused it): its tint clears now.
  // A pointer that arrives in the moment after an apply is the list scrolling under a
  // stationary cursor, not the reader looking at the row, so it is ignored; a click or the
  // reader's own focus counts straight away.
  const onSeen = useCallback(
    (item, how) => {
      if (programmaticFocusRef.current) return;
      if (how === "pointer") {
        const sinceApply = Date.now() - appliedAtRef.current;
        if (sinceApply >= 0 && sinceApply < SEEN_GRACE_MS) return;
      }
      if (freshRef.current.delete(item.id)) bumpFresh();
    },
    [bumpFresh],
  );

  // Applying the pill is the one moment the order may change: swap in the fresh first
  // page, whichever page the reader was on, bring the list to the top and put focus on its
  // first row.
  const applyIncoming = useCallback(() => {
    if (!incoming) return;
    const next = reconcileSnapshot(incoming.items, incoming.takenAt);
    // "New" is decided now, against the first page as displayed: the rows this apply adds.
    // Rows already shown, even if they moved, keep whatever they had, and a tinted row the
    // snapshot no longer holds drops its entry.
    const known = page1KnownRef.current ?? new Set();
    const nextIds = new Set(next.map((r) => r.id));
    const appliedAt = Date.now();
    appliedAtRef.current = appliedAt;
    freshRef.current.forEach((_, id) => {
      if (!nextIds.has(id)) freshRef.current.delete(id);
    });
    next.forEach((r) => {
      if (!known.has(r.id)) freshRef.current.set(r.id, appliedAt);
    });
    bumpFresh();
    requestRef.current += 1; // a page request still out is superseded
    requestOutRef.current = false;
    page1KnownRef.current = nextIds;
    setPage(1);
    setPageOffset(0);
    removedOnPageRef.current = 0;
    failedResetRef.current = false;
    setFeedLoading(false);
    setFeedFailed(false);
    setItems(next);
    setTotals((t) => ({ ...t, [everyone ? "everyone" : "mine"]: incoming.total }));
    focusFirstRef.current = true;
    setIncoming(null);
  }, [incoming, everyone, reconcileSnapshot, bumpFresh]);

  // After a page change or the pill lands: bring the list's top into view, and for the pill
  // also put focus on its first row. A plain page change never moves focus.
  useEffect(() => {
    const focusFirst = focusFirstRef.current;
    if (!focusFirst && !scrollTopRef.current) return;
    focusFirstRef.current = false;
    scrollTopRef.current = false;
    const list = listRef.current;
    list?.scrollIntoView?.({ block: "start" });
    if (!focusFirst) return;
    programmaticFocusRef.current = true;
    try {
      list?.querySelector("[data-now-row]")?.focus();
    } finally {
      programmaticFocusRef.current = false;
    }
  }, [items]);

  const removeItem = useCallback((id) => {
    removedRef.current.set(id, Date.now());
    page1KnownRef.current?.delete(id);
    if (itemsRef.current.some((it) => it.id === id)) removedOnPageRef.current += 1;
    if (freshRef.current.delete(id)) bumpFresh();
    setItems((list) => list.filter((it) => it.id !== id));
  }, [bumpFresh]);

  // Patch one loaded row in place (same index, same order). Used by Assign: the row
  // stays where the server ranked it even if it now belongs to another scope — the next
  // load reconciles that.
  const patchItem = useCallback((id, patch) => {
    patchesRef.current.set(id, {
      patch: { ...patchesRef.current.get(id)?.patch, ...patch },
      at: Date.now(),
    });
    setItems((list) => list.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  // Drop a coloured outcome strip into the rank slot, then commit the RPC when the Undo
  // window expires. `commit` is fire-and-forget with a verbatim error banner on refusal
  // (e.g. request_unanswered "deliver it, don't dismiss").
  const startPending = useCallback((item, { tone, label, commit }) => {
    setPending((p) => ({ ...p, [item.id]: { item, tone, label } }));
    // Lift the strip out of the rank slot, whichever way the commit lands.
    const clearStrip = () =>
      setPending((p) => {
        const n = { ...p };
        delete n[item.id];
        return n;
      });
    timersRef.current[item.id] = setTimeout(() => {
      delete timersRef.current[item.id];
      // Gate removal on the RPC, exactly like a terminal `exit`: the row leaves only
      // once the commit RESOLVES. On refusal, restore the row and surface the message
      // verbatim — otherwise a rejected Done/Dismiss silently drops the signal until a full
      // reload. The strip is keyed by id and the timer outlives the page: a reader who pages
      // away meanwhile no longer sees it, but the dismissal still commits at the end of the
      // window, and the strip is back if they return before then.
      Promise.resolve(commit())
        .then(() => {
          setActionError(null);
          clearStrip();
          removeItem(item.id);
        })
        .catch((e) => {
          clearStrip();
          setActionError(e.message);
          // eslint-disable-next-line no-console
          console.error("[NowPage] action failed", e);
        });
    }, DISMISS_UNDO_MS);
  }, [removeItem]);

  // The row's dismiss control (No longer needed / Resolve) → a red strip.
  const onDismiss = useCallback(
    (item) =>
      startPending(item, {
        tone: "dismiss",
        label: "Dismissed",
        commit: () => dismissNow({ kind: item.kind, id: item.id }),
      }),
    [startPending],
  );

  // Marking a promise done → a green strip (a kept promise, not a dismissal).
  const onMet = useCallback(
    (item) =>
      startPending(item, {
        tone: "met",
        label: "Done",
        commit: () => markPromiseMet(item),
      }),
    [startPending],
  );

  const onUndo = useCallback((item) => {
    const t = timersRef.current[item.id];
    if (t) clearTimeout(t);
    delete timersRef.current[item.id];
    // Restore the card at its rank — no API call is ever fired.
    setPending((p) => {
      const n = { ...p };
      delete n[item.id];
      return n;
    });
  }, []);

  useEffect(
    () => () => {
      Object.values(timersRef.current).forEach(clearTimeout);
    },
    [],
  );

  // A kind's EXIT action fires its own RPC and optimistically removes the row.
  // markPromiseMet et al. are the signal's real exits — NOT dismiss.
  const handlers = useMemo(() => {
    // A terminal exit removes the row ONLY after its RPC resolves. Removing first would
    // silently drop the signal on failure — the row would stay gone from the list until the
    // page is next fetched, with nothing to say why, so a rejected Done/Chased/Release/Reply
    // would vanish unexplained.
    const exit = (apiFn) => (item) =>
      Promise.resolve(apiFn(item))
        .then(() => {
          setActionError(null);
          removeItem(item.id);
        })
        .catch((e) => {
          // The RPC refused (e.g. fn_agent_release on a non-draft message, or a task
          // that says "move the work with fn_work_move") — show its message verbatim.
          setActionError(e.message);
          // eslint-disable-next-line no-console
          console.error("[NowPage] action failed", e);
        });
    const fire = (apiFn) => (item) =>
      Promise.resolve(apiFn(item))
        .then(() => setActionError(null))
        .catch((e) => {
          setActionError(e.message);
          // eslint-disable-next-line no-console
          console.error("[NowPage] action failed", e);
        });
    return {
      // Done → a green at-rank strip with Undo (handled in NowPage, not a plain exit).
      markPromiseMet: onMet,
      markChased: exit(chaseClient),
      releaseAgent: exit(releaseAgent),
      // Edit returns to draft — a distinct RPC, never release (spec §4). It is the row's
      // only control for an agent draft, so it clears the row like Release does: after the
      // RPC resolves, and never before, so a refusal leaves the row and shows its message.
      editAgent: exit(editAgent),
      // needs_assistance → <MoveWorkMenu> calls work.js moveWork directly (D4 / §A-1);
      // moving to a non-blocked status resolves the signal, so the row leaves only
      // after the RPC resolves (the menu calls onMoved → this handler).
      onWorkMoved: (item) => removeItem(item.id),
      // Assign writes the owner server-side, then patches it into the row in place. A
      // refusal (e.g. fn_thread_assign on a DM) leaves the row untouched and is shown
      // verbatim.
      onAssigned: (item, patch) => {
        setActionError(null);
        patchItem(item.id, patch);
      },
      onAssignError: (message) => setActionError(message),
      openRequest: fire(openRequest),
    };
  }, [removeItem, onMet, patchItem]);

  const openPanel = useCallback((item) => {
    setPanelError(null);
    setPanelBusy(false);
    setTaskDraft(null);
    setPanelSeq((n) => n + 1);
    setOpenPromise({ id: item.id, dismissLabel: item.dismiss_label });
  }, []);

  const closePanel = useCallback(() => {
    restoreFocusRef.current = openPromiseRef.current?.id ?? null;
    setOpenPromise(null);
    setPanelError(null);
    setPanelBusy(false);
    setTaskDraft(null);
  }, []);

  // Closing hands focus back to the row's headline button; when that row has left the list
  // (Done / No longer needed remove it) to the list, and when the list is not mounted (the
  // all-clear or error card) to the page heading.
  useEffect(() => {
    if (openPromise || restoreFocusRef.current == null) return;
    const id = restoreFocusRef.current;
    restoreFocusRef.current = null;
    const list = listRef.current;
    const headline = list
      ? Array.from(list.querySelectorAll("[data-now-open]")).find(
          (el) => el.closest("[data-now-row]")?.getAttribute("data-now-row") === id,
        )
      : null;
    (headline || list || headingRef.current)?.focus();
  }, [openPromise]);

  // A panel action writes for ONE promise; what it reports back is only shown while that
  // promise is still the open one.
  const isOpenPromise = useCallback((id) => openPromiseRef.current?.id === id, []);

  /** Runs `work` with the promise marked in flight, so no second write, create or link can start for it. */
  const withInFlight = useCallback(
    async (promiseId, work) => {
      inFlightRef.current.add(promiseId);
      bumpTasks();
      try {
        return await work();
      } finally {
        inFlightRef.current.delete(promiseId);
        bumpTasks();
      }
    },
    [bumpTasks],
  );

  /**
   * Done / No longer needed from the panel commit at once (no Undo window): the panel has
   * to show the new status. The row leaves through `removeItem`, which also keeps a stale
   * poll snapshot from bringing it back and counts toward the Next-offset allowance only
   * while the row is on the page on screen. A refusal leaves the row and shows its message
   * verbatim in the panel. The panel stays busy after a success until its refetch lands, so
   * the buttons never show flags that are out of date.
   */
  const panelAction = useCallback(
    (write) => async () => {
      const open = openPromiseRef.current;
      if (!open) return;
      setPanelBusy(true);
      setPanelError(null);
      await withInFlight(open.id, async () => {
        try {
          await write(open);
          removeItem(open.id);
          if (isOpenPromise(open.id)) {
            // Busy until the refetch lands, even if the panel was reopened meanwhile.
            setPanelBusy(true);
            setPanelReloadKey((k) => k + 1);
          }
        } catch (e) {
          if (isOpenPromise(open.id)) {
            setPanelError(e?.message || "That didn't go through.");
            setPanelBusy(false);
          }
          // eslint-disable-next-line no-console
          console.error("[NowPage] panel action failed", e);
        }
      });
    },
    [removeItem, isOpenPromise, withInFlight],
  );
  const onPanelDone = useMemo(() => panelAction((open) => markPromiseMet({ id: open.id })), [panelAction]);
  const onPanelNoLongerNeeded = useMemo(
    // The kind is the spec's literal: a promise is withdrawn as `promise_broken`.
    () => panelAction((open) => dismissNow({ kind: "promise_broken", id: open.id })),
    [panelAction],
  );
  /**
   * A load landed: the panel is no longer busy. If it shows the task this promise was
   * recorded as having an unlinked, the link did go through after all (the reply was lost),
   * so the record and its "link failed" message are dropped.
   */
  const onPanelLoaded = useCallback(
    (p) => {
      setPanelBusy(false);
      const id = openPromiseRef.current?.id;
      const recorded = id != null ? unlinkedTasks.get(id) : undefined;
      if (recorded != null && p?.task?.id === recorded) {
        unlinkedTasks.delete(id);
        setPanelError((cur) => (cur && cur.startsWith(LINK_FAILED) ? null : cur));
        bumpTasks();
      }
    },
    [bumpTasks],
  );

  /**
   * Pipelines and owners for the task form, fetched on the first "Make it a task" rather
   * than on page load. A failed read is logged and leaves that list empty: with no pipeline
   * choices the form falls back to its default pipeline, and the next open reads again.
   */
  const loadTaskFormOptions = useCallback(async () => {
    if (taskFormLoadingRef.current) return;
    taskFormLoadingRef.current = true;
    const [types, members] = await Promise.all([
      getWorkTypes().then((r) => r?.data || []).catch((e) => {
        // eslint-disable-next-line no-console
        console.error("[NowPage] work types failed", e);
        return null;
      }),
      getAssignableMembers().catch((e) => {
        // eslint-disable-next-line no-console
        console.error("[NowPage] assignable members failed", e);
        return null;
      }),
    ]);
    taskFormLoadingRef.current = false;
    setTaskFormOptions({
      workTypeOptions: types ? toWorkTypeOptions(types) : [],
      owners: members || [],
      complete: !!types && !!members,
    });
  }, []);

  const onPanelMakeTask = useCallback(
    (p) => {
      const open = openPromiseRef.current;
      if (!open) return;
      setPanelError(null);
      setTaskDraft({ id: open.id, p });
      if (!taskFormOptions?.complete) loadTaskFormOptions();
    },
    [taskFormOptions, loadTaskFormOptions],
  );

  // Closes the task form only if it is still the one for `id`: a form for another promise
  // that has since opened is not the caller's to close.
  const closeTaskForm = useCallback(
    (id) => setTaskDraft((cur) => (cur && cur.id === id ? null : cur)),
    [],
  );

  /**
   * Ties the created task to the promise and refreshes both views: the panel's detail and
   * the Now page on screen, at its own offset (the path Try again uses; skipped while another
   * page request is out, which will read fresh anyway). A failure records the task id under
   * the promise, before anything else, so Retry link repeats ONLY the link and a reopened
   * panel can never offer a second create.
   */
  const linkTask = useCallback(
    async (promiseId, taskId) => {
      if (isOpenPromise(promiseId)) setPanelBusy(true);
      try {
        await linkPromiseTask({ id: promiseId, taskId });
      } catch (e) {
        unlinkedTasks.set(promiseId, taskId);
        bumpTasks();
        if (isOpenPromise(promiseId)) {
          setPanelError(`${LINK_FAILED}${e?.message || "unknown error"}`);
          setPanelBusy(false);
        }
        // eslint-disable-next-line no-console
        console.error("[NowPage] promise link failed", e);
        return;
      }
      unlinkedTasks.delete(promiseId);
      bumpTasks();
      if (isOpenPromise(promiseId)) {
        setPanelError(null);
        setPanelBusy(true); // until the refetch lands, even if the panel was reopened meanwhile
        setPanelReloadKey((k) => k + 1);
      }
      // A page request already out may have been sent before the link committed, so a row can
      // show stale link state until the next page load. The stakes are low: linking does not
      // change which rows are on the page.
      if (!requestOutRef.current) loadPage(pageRef.current, { offset: pageOffsetRef.current });
    },
    [loadPage, isOpenPromise, bumpTasks],
  );

  /**
   * The task form's `onCreate`: create the task, then link it. Returns the new id whenever
   * a task exists (even if the link failed) so the form's file uploads still run, and
   * `undefined` when nothing was created or its id could not be read (the form just closes;
   * a created-but-unidentified task is not reported as a failed create, which would invite
   * a duplicate). The promise is the one the form was opened for, whatever panel is open by
   * the time the create answers.
   */
  const onCreateFromPromise = useCallback(
    (payload) => {
      const promiseId = taskDraft.id;
      const say = (message) => {
        if (isOpenPromise(promiseId)) setPanelError(message);
      };
      return withInFlight(promiseId, async () => {
        let taskId;
        try {
          const resp = await createWork(payload);
          if (resp?.data?.error) {
            say(resp.data.message || "Failed to create the task.");
            return undefined;
          }
          taskId = createdTaskOf(resp);
        } catch (e) {
          say(e?.message || "Failed to create the task.");
          // eslint-disable-next-line no-console
          console.error("[NowPage] task create failed", e);
          return undefined;
        }
        if (!taskId) {
          // A task exists but cannot be named: remember that, so no second one is offered.
          unlinkedTasks.set(promiseId, null);
          bumpTasks();
          return undefined;
        }
        await linkTask(promiseId, taskId);
        return taskId;
      });
    },
    [taskDraft, linkTask, withInFlight, isOpenPromise, bumpTasks],
  );

  const taskInitialValues = useMemo(() => {
    if (!taskDraft) return undefined;
    const { p } = taskDraft;
    const text = (v) => (typeof v === "string" ? v : "");
    const subject = text(p.conversation?.subject);
    const description = [text(p.source?.text), subject ? `Promised in: ${subject}` : ""]
      .filter(Boolean)
      .join("\n\n");
    return {
      title: text(p.what),
      client_id: p.client?.id ?? null,
      description,
    };
  }, [taskDraft]);
  const taskFilterOptions = useMemo(
    () => ({
      owners: taskFormOptions?.owners || [],
      services: [],
      clients: taskDraft?.p?.client?.id
        ? [{ id: taskDraft.p.client.id, name: typeof taskDraft.p.client.name === "string" ? taskDraft.p.client.name : "" }]
        : [],
    }),
    [taskFormOptions, taskDraft],
  );

  // The count beside the active tab is that scope's own server total (Mine's total on the
  // Mine tab, Everyone's on the Everyone tab) — deliberately NOT the visible-row count, so
  // it does not tick down on an optimistic dismiss/exit; the next poll/load reconciles it.
  const scopeTotal = totals[everyone ? "everyone" : "mine"] ?? items.length;
  // What the header shows and the sidebar badge is given: the scope's server total once it is
  // known, and null while it is not (its count failed to load), never a row count passed off as
  // it. The header then shows no count, and the sidebar falls back to its polled count.
  const knownTotal = totals[everyone ? "everyone" : "mine"];
  const shownTotal = typeof knownTotal === "number" ? knownTotal : null;
  // The server total, so rows waiting behind the "N new" pill already count toward the page
  // count: a pager that is honest about what exists, not about what is applied yet.
  const pageCount = Math.max(1, Math.ceil(scopeTotal / PAGE_SIZE));
  // The page the pager shows, held inside the range while a shrinking total is being caught up.
  const currentPage = Math.min(page, pageCount);
  // Read at render: the expiry timer re-renders at the earliest expiry, and any other
  // render in between sees an expired entry as already gone.
  const renderedAt = Date.now();
  const isFresh = (id) => {
    const at = freshRef.current.get(id);
    return at !== undefined && renderedAt - at < NEW_ROW_MS;
  };
  // The team's count for the Mine-empty link. Everyone contains Mine, so it is only honest
  // once the total was read after the reader's clears reached the server — the refetch that
  // follows an emptied page re-reads it.
  const teamCount = totals.everyone ?? 0;

  // The page the emptied-page refetch would ask for, and the key that makes it a one-off.
  const refetchPage = Math.min(page, pageCount);
  const refetchKey = `${everyone}:${refetchPage}:${scopeTotal}`;
  // A page that has nothing to show while the server still has rows — the reader cleared
  // it, or the total shrank past it — is refetched (the last page, when this one is gone)
  // instead of being read as "all clear". A pill waiting on page 1 holds that back: applying
  // it is the reader's to do. On a later page the pill does not touch the list, so it does not.
  useEffect(() => {
    if (status !== "ready" || feedLoading || requestOutRef.current || feedFailed) return;
    if (incoming && refetchPage === 1) return;
    const emptied = items.length === 0 && scopeTotal > 0;
    const beyond = page > pageCount;
    if (!emptied && !beyond) {
      autoRefetchRef.current = null;
      return;
    }
    if (autoRefetchRef.current === refetchKey) return;
    autoRefetchRef.current = refetchKey;
    loadPage(refetchPage, {
      offset: refetchPage === page ? pageOffset : (refetchPage - 1) * PAGE_SIZE,
      probeOther: true,
    });
  }, [status, feedLoading, feedFailed, incoming, items.length, scopeTotal, page, pageOffset, pageCount, refetchPage, refetchKey, loadPage]);

  /**
   * Previous / Next: fetches that page's window; the list scrolls back to its top once it lands.
   * The window follows the server's list as it is now: rows the reader cleared have shifted it
   * up, so Next starts earlier by that many rows, and no row is skipped.
   */
  const goToPage = (target) =>
    loadPage(target, {
      scroll: true,
      offset:
        target > currentPage
          ? pageOffset + PAGE_SIZE - removedOnPageRef.current
          : Math.max(0, pageOffset - PAGE_SIZE),
    });

  // What an empty list says when the page is idle and the server (or the pill) still has rows:
  // the pill's rows are the reader's to apply, and rows the refetch could not find stay counted.
  const waitingNote =
    items.length === 0 && !feedLoading && !feedFailed
      ? incoming
        ? `${incoming.count} new waiting.`
        : scopeTotal > 0 && autoRefetchRef.current === refetchKey
          ? `${scopeTotal} more waiting.`
          : null
      : null;
  return (
    <HubShell nowCount={status === "ready" ? shownTotal : null}>
      <div className="px-4 py-6 md:px-6 lg:px-10">
        {/* The header is the title, the active scope's count and the scope switch, in one row
            that stacks (title and count over the switch) below md. The switch is built like the
            Clients page's view tabs: one bordered bar with flush segments and a solid-blue active
            block, each with its own small count. Kept as toggle buttons (aria-pressed), not tabs,
            since there is no tabpanel per scope — the same feed re-renders. */}
        <header className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
          <div className="flex items-baseline gap-3">
            <h1
              ref={headingRef}
              tabIndex={-1}
              className="text-2xl font-bold text-gray-900 focus:outline-none dark:text-gray-100"
            >
              Now
            </h1>
            {status === "ready" && shownTotal !== null && (
              <span
                data-testid="now-header-count"
                className="text-xl font-semibold tabular-nums text-gray-500 dark:text-gray-400"
              >
                {fmtNumber(shownTotal)}
                <span className="sr-only">
                  {`${shownTotal === 1 ? " item" : " items"} ${everyone ? "for everyone" : "for you"}`}
                </span>
              </span>
            )}
          </div>

          {status === "ready" && (
            <div
              role="group"
              className="inline-flex self-start overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 md:self-auto"
              aria-label="Scope"
            >
              {[
                { value: false, label: "Mine" },
                { value: true, label: "Everyone" },
              ].map((s) => {
                const active = everyone === s.value;
                // Each segment shows ITS OWN scope total, always — not only the active
                // one — so both counts stay visible and neither jumps in/out on a
                // switch. The active one is kept current by the poll and every page
                // fetch; the inactive one by its total-only probe.
                const count = totals[s.value ? "everyone" : "mine"];
                return (
                  <button
                    key={s.label}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setEveryone(s.value)}
                    className={`px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand ${
                      active
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
                    }`}
                  >
                    {s.label}
                    {typeof count === "number" && (
                      <span
                        data-testid="now-count"
                        className={`ml-2 text-xs font-normal tabular-nums ${
                          active ? "text-blue-200" : "text-gray-400 dark:text-gray-500"
                        }`}
                      >
                        {fmtNumber(count)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </header>

        {status === "loading" && (
          <div
            role="status"
            aria-label="Loading"
            className="mt-6 space-y-2"
          >
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-16 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-900"
              />
            ))}
          </div>
        )}

        {status === "gated" && (
          <div className="mt-6">
            <NotPermitted />
          </div>
        )}

        {status === "error" && (
          <div className="mt-6">
            <ErrorCard onRetry={loadAll} />
          </div>
        )}

        {status === "ready" && (
          <>
            {takeaway && (
              <InsightCard says={takeaway.says} tint={takeaway.tint} />
            )}

            {/* The "N new" pill sits at the right above the list. Its row is always the pill's
                height, so the pill arriving moves nothing, and the live region stays mounted so
                its arrival is announced. */}
            <div className="mt-4 flex min-h-[34px] items-center justify-end">
              <div aria-live="polite" data-testid="now-new-live">
                {incoming && (
                  <button
                    type="button"
                    data-testid="now-new-pill"
                    title="Show the new items at the top"
                    onClick={applyIncoming}
                    className="rounded-full border border-blue-200 bg-blue-100 px-4 py-1.5 text-sm font-semibold tabular-nums text-blue-700 transition-colors hover:bg-blue-200 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                  >
                    <span aria-hidden="true">↑ </span>
                    {`${incoming.count} new`}
                  </button>
                )}
              </div>
            </div>

            {actionError && (
              <div
                role="alert"
                data-testid="now-action-error"
                className="mt-4 flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
              >
                <span>{actionError}</span>
                <button
                  type="button"
                  aria-label="Dismiss message"
                  onClick={() => setActionError(null)}
                  className="shrink-0 font-medium"
                >
                  &times;
                </button>
              </div>
            )}

            <PanelErrorBoundary label="now-feed">
              {feedFailed && !feedLoading ? (
                <div className="mt-6" data-testid="now-feed-error">
                  <ErrorState
                    variant="card"
                    message={`Couldn't load ${everyone ? "everyone's" : "your"} items. The request failed or took too long.`}
                    onRetry={() => loadPage(page, { offset: pageOffset, scroll: true })}
                  />
                </div>
              ) : items.length === 0 && scopeTotal === 0 && !incoming && !feedLoading ? (
                <AllClear
                  teamCount={teamCount}
                  onShowTeam={
                    !everyone && teamCount > 0 ? () => setEveryone(true) : undefined
                  }
                />
              ) : (
                <>
                  {waitingNote && (
                    <p
                      data-testid="now-cleared"
                      className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      {waitingNote}
                    </p>
                  )}
                  <div className="relative">
                    <ul
                      ref={listRef}
                      tabIndex={-1}
                      className={`mt-4 focus:outline-none transition-opacity ${
                        items.length > 0 ? LIST_SURFACE : ""
                      } ${feedLoading ? "opacity-40" : ""} ${
                        // An empty list that is being refetched still has room for the indicator.
                        items.length === 0 && feedLoading ? "min-h-[6rem]" : ""
                      }`}
                      aria-busy={feedLoading}
                    >
                      {items.map((item) => (
                        <li key={item.id} className={ROW_DIVIDER}>
                          {pending[item.id] ? (
                            <UndoStrip entry={pending[item.id]} onUndo={onUndo} />
                          ) : (
                            <NowRow
                              item={item}
                              handlers={handlers}
                              onDismiss={onDismiss}
                              scope={everyone ? "everyone" : "mine"}
                              isNew={isFresh(item.id)}
                              onSeen={onSeen}
                              onOpen={openPanel}
                              onOpenClient={showClient}
                            />
                          )}
                        </li>
                      ))}
                    </ul>

                    {/* Refresh indicator floating over the fading records. */}
                    {feedLoading && (
                      <div
                        role="status"
                        aria-label="Refreshing"
                        className="pointer-events-none absolute inset-0 flex items-center justify-center"
                      >
                        <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
                          <svg
                            className="h-3.5 w-3.5 animate-spin text-[#32B1FF]"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                          Refreshing…
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Pager — the Clients list's, below the list and outside its surface. Previous /
                  Next fetch a server page; both disable while one is out. It stays under the
                  error card of a failed page so the reader can step back. */}
              {pageCount > 1 && (
                <nav
                  data-testid="now-pager"
                  className="flex items-center justify-between gap-3 pt-4"
                  aria-label="Pagination"
                >
                  <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                    {/* The range starts at the page's server offset and reads the server total, so
                        it can run ahead of the rows on screen after the reader clears some. */}
                    {fmtNumber(pageOffset + 1)}–{fmtNumber(Math.min(pageOffset + PAGE_SIZE, scopeTotal))} of {fmtNumber(scopeTotal)}
                  </span>
                  <div className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage <= 1 || feedLoading}
                      className="px-3 py-1.5 rounded-lg border text-sm border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                      Page {fmtNumber(currentPage)} of {fmtNumber(pageCount)}
                    </span>
                    <button
                      type="button"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage >= pageCount || feedLoading}
                      className="px-3 py-1.5 rounded-lg border text-sm border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </nav>
              )}
            </PanelErrorBoundary>
          </>
        )}
      </div>

      {/* The client slide-out, in the kit's controlled mode: one instance serves every row. */}
      <PanelErrorBoundary key={openClient?.seq ?? "closed"} label="now-client-slideout">
        <ClientSlideOut
          open={!!openClient}
          clientId={openClient?.clientId}
          clientName={openClient?.clientName}
          onClose={() => setOpenClient(null)}
        />
      </PanelErrorBoundary>

      {/* Keyed per open so a crashed panel is replaced, not left as its fallback. */}
      <PanelErrorBoundary key={panelSeq} label="now-promise-panel">
        {openPromise && (
          <PromisePanel
            promiseId={openPromise.id}
            dismissLabel={openPromise.dismissLabel}
            reloadKey={panelReloadKey}
            onClose={closePanel}
            onDone={onPanelDone}
            onNoLongerNeeded={onPanelNoLongerNeeded}
            onMakeTask={onPanelMakeTask}
            onRetryLink={
              typeof unlinkedTasks.get(openPromise.id) === "string"
                ? () => withInFlight(openPromise.id, () => linkTask(openPromise.id, unlinkedTasks.get(openPromise.id)))
                : undefined
            }
            taskLost={unlinkedTasks.has(openPromise.id) && unlinkedTasks.get(openPromise.id) === null}
            hideMakeTask={unlinkedTasks.has(openPromise.id) || inFlightRef.current.has(openPromise.id)}
            escDisabled={!!taskDraft}
            onLoaded={onPanelLoaded}
            busy={panelBusy || inFlightRef.current.has(openPromise.id)}
            error={panelError}
          />
        )}
        <CreateTaskModal
          key={taskDraft?.id ?? "closed"}
          open={!!taskDraft}
          onClose={() => closeTaskForm(taskDraft?.id)}
          onCreate={onCreateFromPromise}
          workTypeOptions={taskFormOptions?.workTypeOptions || []}
          allFilterOptions={taskFilterOptions}
          initialValues={taskInitialValues}
        />
      </PanelErrorBoundary>
    </HubShell>
  );
}
